import React, { useMemo } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import type { RestaurantOverviewSummary } from '@/models/restaurant-manager'

type Props = {
  summary: RestaurantOverviewSummary
}

export const RestaurantSubjectSummaryPanel: React.FC<Props> = ({ summary }) => {
  const { t } = useTranslation()
  const nightColumns = useMemo(() => [...summary.nightColumns].sort((left, right) => left - right), [summary.nightColumns])
  const gridTemplateColumns = useMemo(
    () => `${subjectSummaryColumnWidth} repeat(${nightColumns.length}, ${summaryCountColumnWidth}) ${summaryTotalColumnWidth}`,
    [nightColumns.length],
  )

  if (summary.items.length === 0 && summary.nightColumns.length === 0) {
    return <div>{t('No data available')}</div>
  }

  return (
    <div css={tableWrapperStyle}>
      <div css={[tableStyle, { gridTemplateColumns }]}>
        <div className="head subject">{t('RestaurantManager.ColumnSubjectName')}</div>
        {nightColumns.map(night => (
          <div key={night} className="head numeric">
            {t('RestaurantManager.NightIndexValue', { count: night })}
          </div>
        ))}
        <div className="head numeric">{t('Total')}</div>

        {summary.items.map(item => (
          <React.Fragment key={item.subjectId}>
            <div className="cell subject">{item.subjectName}</div>
            {nightColumns.map(night => (
              <div key={`${item.subjectId}-${night}`} className="cell numeric">
                {item.countsByNight[night.toString()] ?? 0}
              </div>
            ))}
            <div className="cell numeric total">{item.total}</div>
          </React.Fragment>
        ))}

        <div className="footer subject">{t('RestaurantManager.nightTotal')}</div>
        {nightColumns.map(night => (
          <div key={`footer-${night}`} className="footer numeric">
            {summary.items.reduce((total, item) => total + (item.countsByNight[night.toString()] ?? 0), 0)}
          </div>
        ))}
        <div className="footer numeric total">{summary.grandTotal}</div>
      </div>
    </div>
  )
}

const tableWrapperStyle = css({
  maxWidth: 700,
  overflowX: 'scroll',
  overflowY: 'hidden',
  width: '100%',
  '&::-webkit-scrollbar': {
    height: 8,
  },
  '&::-webkit-scrollbar-track': {
    background: '#F5F5F5',
    borderRadius: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#C1C1C1',
    borderRadius: 4,
    '&:hover': {
      background: '#A8A8A8',
    },
  },
  scrollbarColor: '#C1C1C1 #F5F5F5',
  scrollbarWidth: 'thin',
  padding: '0 24px',
})

const subjectSummaryColumnWidth = '250px'
const summaryCountColumnWidth = '72px'
const summaryTotalColumnWidth = '72px'

const tableStyle = css({
  border: '1px solid #F2F2F2',
  display: 'inline-grid',
  minWidth: 'max-content',
  width: 'max-content',
  '.head': {
    background: '#F5F5F5',
    borderBottom: '1px solid #F2F2F2',
    borderRight: '1px solid #F2F2F2',
    color: '#676767',
    fontSize: 12,
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    ':last-of-type': {
      borderRight: 'none',
    },
    '&.subject': {
      overflowWrap: 'anywhere',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      padding: '10px 16px',
    },
    '&.numeric': {
      padding: '10px 12px',
      whiteSpace: 'nowrap',
    },
  },
  '.cell': {
    borderBottom: '1px solid #F2F2F2',
    borderRight: '1px solid #F2F2F2',
    color: '#272727',
    fontSize: 13,
    minHeight: 31,
    display: 'flex',
    alignItems: 'center',
    ':nth-last-of-type(1)': {
      borderRight: 'none',
    },
    '&.subject': {
      overflowWrap: 'anywhere',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      padding: '8px 16px',
    },
    '&.numeric': {
      padding: '8px 12px',
      justifyContent: 'flex-end',
      whiteSpace: 'nowrap',
    },
  },
  '.footer': {
    background: '#F5F5F5',
    color: '#676767',
    borderRight: '1px solid #F2F2F2',
    fontSize: 13,
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    '&.subject': {
      overflowWrap: 'anywhere',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      padding: '10px 16px',
    },
    '&.numeric': {
      padding: '10px 12px',
      justifyContent: 'flex-end',
      whiteSpace: 'nowrap',
    },
  },
})
